import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';

import { formatDay } from './days';

/**
 * Home's hand-built charts (SR-485) — no chart library: a sparkline of one or more series on one
 * axis, and an inline bar for a share. The marks follow the dataviz method: 2 px lines with a 10 %
 * wash under a lone series, an 8 px end dot ringed in the surface colour, a hairline baseline, and
 * a crosshair that snaps to the nearest day with every series' value in its tooltip. Text never
 * wears a series colour; a short line key beside it carries the identity.
 *
 * The series colours are the brand's: Sapphire, then Turquoise mixed half into Teal — validated as
 * a pair on white (`validate_palette.js`, light: every check passes, CVD ΔE 22.7).
 */
export const SERIES_COLORS = [
  'var(--color-sapphire)',
  'color-mix(in srgb, var(--color-turquoise) 50%, var(--color-teal))'
] as const;

const GRID = 'color-mix(in srgb, var(--color-royal-blue) 12%, white)';

export interface ISparkSeries {
  label: string;
  values: readonly number[];
}

/** The element's rendered width, followed as it changes. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = React.useRef<T>(null);
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry?.contentRect.width ?? 0)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/**
 * Up to two series over `days` on one axis from 0 to the larger peak. Hover or arrow keys pick a
 * day; its values are read in the tooltip, and every value is in the table a screen reader reads.
 */
export function Sparkline({
  days,
  series,
  label,
  height = 44
}: {
  days: readonly string[];
  series: readonly ISparkSeries[];
  /** What the chart shows, its accessible name. */
  label: string;
  height?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [at, setAt] = React.useState<Nullable<number>>(null);
  const count = days.length;
  const peak = Math.max(1, ...series.flatMap((one) => one.values));
  const pad = 5;
  const x = (index: number) => (count < 2 ? width / 2 : pad + (index * (width - 2 * pad)) / (count - 1));
  const y = (value: number) => pad + (height - 2 * pad) * (1 - value / peak);
  const nearest = (clientX: number) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box || count === 0) return null;
    const ratio = (clientX - box.left - pad) / Math.max(1, box.width - 2 * pad);
    return Math.min(count - 1, Math.max(0, Math.round(ratio * (count - 1))));
  };
  const shown = at ?? null;

  return (
    <div ref={ref} className="relative">
      {width > 0 && count > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={label}
          tabIndex={0}
          className="block cursor-crosshair overflow-visible rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onPointerMove={(event) => setAt(nearest(event.clientX))}
          onPointerLeave={() => setAt(null)}
          onFocus={() => setAt((current) => current ?? count - 1)}
          onBlur={() => setAt(null)}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            const step = event.key === 'ArrowLeft' ? -1 : 1;
            setAt((current) => Math.min(count - 1, Math.max(0, (current ?? count - 1) + step)));
          }}
        >
          <line x1={0} x2={width} y1={y(0)} y2={y(0)} stroke={GRID} strokeWidth={1} />
          {series.map((one, index) => {
            const color = SERIES_COLORS[index] ?? SERIES_COLORS[0];
            const points = one.values.map((value, day) => `${x(day)},${y(value)}`);
            return (
              <g key={one.label}>
                {series.length === 1 && (
                  <path
                    d={`M${x(0)},${y(0)} L${points.join(' L')} L${x(one.values.length - 1)},${y(0)} Z`}
                    fill={color}
                    fillOpacity={0.1}
                  />
                )}
                <polyline
                  points={points.join(' ')}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              </g>
            );
          })}
          {shown !== null && (
            <line x1={x(shown)} x2={x(shown)} y1={0} y2={height} stroke="var(--veritant-royal-55)" strokeWidth={1} />
          )}
          {series.map((one, index) => {
            const day = shown ?? one.values.length - 1;
            const value = one.values[day];
            if (value === undefined) return null;
            return (
              <circle
                key={one.label}
                cx={x(day)}
                cy={y(value)}
                r={4}
                fill={SERIES_COLORS[index] ?? SERIES_COLORS[0]}
                stroke="white"
                strokeWidth={2}
              />
            );
          })}
        </svg>
      )}
      {shown !== null && width > 0 && (
        <div
          role="status"
          className="pointer-events-none absolute bottom-full z-10 mb-1 grid min-w-28 -translate-x-1/2 gap-0.5 rounded-md border border-border bg-white px-2 py-1.5 text-xs shadow-md"
          style={{ left: Math.min(width - 56, Math.max(56, x(shown))) }}
        >
          <span className="text-muted-foreground">{formatDay(days[shown] ?? '')}</span>
          {series.map((one, index) => (
            <span key={one.label} className="flex items-center gap-1.5 whitespace-nowrap">
              <LineKey index={index} />
              <span className="font-semibold text-foreground tabular-nums">
                {(one.values[shown] ?? 0).toLocaleString('en-US')}
              </span>
              <span className="text-muted-foreground">{one.label}</span>
            </span>
          ))}
        </div>
      )}
      {/* A table sizes to its cells whatever its width says: clipped inside a box of its own. */}
      <div className="sr-only">
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">Day</th>
              {series.map((one) => (
                <th key={one.label} scope="col">
                  {one.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((day, index) => (
              <tr key={day}>
                <th scope="row">{formatDay(day)}</th>
                {series.map((one) => (
                  <td key={one.label}>{one.values[index] ?? 0}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A series' key: a short stroke of its colour, as the line is drawn. */
export function LineKey({ index, className }: { index: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block h-0.5 w-3 shrink-0 rounded-full', className)}
      style={{ background: SERIES_COLORS[index] ?? SERIES_COLORS[0] }}
    />
  );
}

/** A share as a thin bar on a lighter track of its own hue; `share` is 0..1. */
export function InlineBar({ share, className }: { share: number; className?: string }) {
  const width = `${Math.round(Math.min(1, Math.max(0, share)) * 1000) / 10}%`;
  return (
    <span
      aria-hidden
      className={cn('block h-1.5 overflow-hidden rounded-full', className)}
      style={{ background: 'color-mix(in srgb, var(--color-sapphire) 12%, white)' }}
    >
      <span className="block h-full rounded-full bg-[var(--color-sapphire)]" style={{ width }} />
    </span>
  );
}
